import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'src/store';
import { runtimeConfigs } from 'src/config/runtimeConfigs';
import { MODULE } from 'src/config';
import { CryptaExtFR } from '@cigam/template/dist/utils/cryptaExtFR';
import {
  getAccessToken,
  loginWithCodeVerification,
  loginWithUserNameAndPassword,
  logout as logoutService,
} from '../services/authService';
import { loadAllDireitos } from '../services/direitosService';
import { UserDTO } from '../types/dtos/auth';
import {
  buscarAplicacoesWeb,
  verificarConectividade,
  buscarDadosSobre,
} from '../services/ambienteService';
import { AplicacoesWebDTO, SobreDTO } from '../types/dtos/genericos';
import { verificarAutenticacaoA2F } from '../services/pessoaService';

export interface AccountState {
  user: UserDTO | null;
  direitos: string[];
  dadosPortais: AplicacoesWebDTO | null;
  tipoLogin: 'cigam' | 'contato';
  possuiA2F?: boolean | null;
  possuiConectividade?: boolean;
  dadosSobre: SobreDTO | null;
  urlEditarPerfil?: string;
}

const initialState: AccountState = {
  user: null,
  direitos: [],
  dadosPortais: null,
  tipoLogin: 'cigam',
  possuiA2F: false,
  possuiConectividade: false,
  dadosSobre: null,
  urlEditarPerfil: '',
};

const slice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    clearAccount(state: AccountState) {
      state.user = null;
      state.dadosPortais = null;
      state.direitos = [];
      state.possuiA2F = false;
      state.possuiConectividade = false;
      state.urlEditarPerfil = '';
    },
    setAccount(state: AccountState, action: PayloadAction<AccountState>) {
      const {
        user,
        tipoLogin,
        dadosPortais,
        possuiA2F,
        possuiConectividade,
        dadosSobre,
        urlEditarPerfil,
      } = action.payload;

      state.user = user;
      state.dadosPortais = dadosPortais;
      state.tipoLogin = tipoLogin || 'cigam';
      state.possuiA2F = possuiA2F;
      state.possuiConectividade = possuiConectividade;
      state.dadosSobre = dadosSobre;
      state.urlEditarPerfil = urlEditarPerfil;
    },
  },
});

export const { reducer } = slice;

export const { setAccount } = slice.actions;

export const login =
  (
    userName: string,
    password: string,
    cryptographyKey: number,
    cryptographyTimeout: number,
    portal: string,
    codigoVerificacao?: string,
    perguntaSecreta?: number,
    respostaSecreta?: string
  ): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.clearAccount());
    const idPaiControleAcesso = Number.parseFloat(
      localStorage.getItem('idPaiControleAcesso') ?? '0'
    );

    const user = await loginWithUserNameAndPassword(
      CryptaExtFR.EncryptString(userName, cryptographyKey, cryptographyTimeout),
      CryptaExtFR.EncryptString(password, cryptographyKey, cryptographyTimeout),
      portal,
      codigoVerificacao,
      perguntaSecreta,
      respostaSecreta,
      idPaiControleAcesso
    );

    const userStorage = localStorage.getItem('user');
    const userData = userStorage ? JSON.parse(userStorage) : '';
    const codigosPermitidos = ['440', '439', '7506', '7302'];

    if (
      userData.CodigoMensagem &&
      codigosPermitidos.includes(userData.CodigoMensagem)
    ) {
      const direitos: any[] = [];

      dispatch(
        slice.actions.setAccount({
          user: userData,
          tipoLogin: 'cigam',
          direitos,
          dadosPortais: null,
          possuiA2F: false,
          possuiConectividade: false,
          dadosSobre: null,
          urlEditarPerfil: '',
        })
      );
    } else {
      const direitos = await loadAllDireitos();
      const hash = getAccessToken();
      const enviroment = await runtimeConfigs();
      const dadosPortais = await buscarAplicacoesWeb(
        `${enviroment.API_URL}`.includes('https')
      );
      const dadosSobre = await buscarDadosSobre();
      let possuiConectividade: boolean;
      possuiConectividade = false;
      const dadosCia = dadosPortais.data.Aplicacoes.filter(x =>
        x.Endereco.includes('cia')
      )[0];
      if (dadosCia !== null) {
        const endereco = dadosCia.Endereco.concat('?cigam=')
          .concat(enviroment.API_URL)
          .concat(hash ? '&hash='.concat(hash) : '');

        const verificaConectividade = await verificarConectividade(
          endereco,
          true
        );
        possuiConectividade = verificaConectividade.data.IsAvailable;
      }

      const verificaA2F = await verificarAutenticacaoA2F(user[0].Codigo);
      const possuiA2F = verificaA2F.data.PossuiAutenticacaoA2F;
      const urlEditarPerfil = window.location.origin.concat(
        `${MODULE.editarperfil.location}/Perfil`
      );

      dispatch(
        slice.actions.setAccount({
          user: user[0],
          tipoLogin: 'cigam',
          direitos,
          dadosPortais,
          possuiA2F,
          possuiConectividade,
          dadosSobre,
          urlEditarPerfil,
        })
      );
    }
  };

export const loginContato =
  (
    portal: string,
    codePerson: string,
    sequence: number,
    codeVerification: string
  ): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.clearAccount());

    const user = await loginWithCodeVerification(
      portal,
      codePerson,
      sequence,
      codeVerification
    );

    const direitos = await loadAllDireitos();

    dispatch(
      slice.actions.setAccount({
        user: user[0],
        tipoLogin: 'contato',
        direitos,
        dadosPortais: null,
        possuiA2F: false,
        possuiConectividade: false,
        dadosSobre: null,
      })
    );
  };

export const logout =
  (): AppThunk =>
  (dispatch): void => {
    logoutService();

    dispatch(slice.actions.clearAccount());
  };
